import { useCallback, useEffect, useState } from "react";
import { aiAgentService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import {
  Lightbulb, Check, X, Loader2, ChevronDown, ChevronRight, ShieldAlert, Send,
} from "lucide-react";
import {
  AgentIcon, RiskBadge, ConfidenceBadge, useTenantScope, TenantPicker, SelectTenantNotice,
  PageHeader, fmtDate,
} from "./shared";

const AGENTS = [
  { code: "", label: "All agents" }, { code: "SALES", label: "Sales" },
  { code: "SUPPORT", label: "Support" }, { code: "CAMPAIGN", label: "Campaign" },
  { code: "RETENTION", label: "Retention" },
];
const STATUSES = ["pending", "accepted", "dismissed"];

function RecCard({ rec, onAccept, onDismiss, busy }) {
  const [open, setOpen] = useState(false);
  const action = rec.action || {};
  const isPending = rec.status === "pending";
  const isExternal = action.isExternalSend;
  return (
    <div className="bg-card border border-border rounded-xl p-4" data-testid={`rec-card-${rec.id}`}>
      <div className="flex items-start gap-3">
        <span className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <AgentIcon code={rec.agentCode} className="h-4 w-4" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <h3 className="text-sm font-bold text-foreground">{rec.title}</h3>
            <div className="flex items-center gap-1.5 flex-wrap">
              <ConfidenceBadge label={rec.confidenceLabel} value={rec.confidence} />
              <RiskBadge level={rec.riskLevel} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{rec.reason}</p>

          {isExternal && (
            <p className="text-[11px] text-amber-600 flex items-center gap-1 mt-2"><Send className="h-3 w-3" /> External message — will be SIMULATED & logged (no real send until live connectors).</p>
          )}

          <button onClick={() => setOpen(!open)} className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mt-2" data-testid={`rec-why-${rec.id}`}>
            {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />} Why this? ({(rec.trace || []).length} signals)
          </button>
          {open && (
            <ul className="mt-1.5 space-y-1 bg-muted/40 rounded-lg p-2.5" data-testid={`rec-trace-${rec.id}`}>
              {(rec.trace || []).map((t, i) => (
                <li key={i} className="text-[11px] text-muted-foreground flex items-start gap-1.5"><span className="text-primary mt-0.5">•</span> {t}</li>
              ))}
              <li className="text-[11px] text-muted-foreground/70 pt-1 border-t border-border">Detected {fmtDate(rec.createdOn)} · {rec.agentCode} agent</li>
            </ul>
          )}

          {isPending && (
            <div className="flex items-center gap-2 mt-3">
              <button onClick={() => onAccept(rec)} disabled={busy === rec.id}
                className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-primary/90 disabled:opacity-50"
                data-testid={`rec-accept-${rec.id}`}>
                {busy === rec.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (action.requiresApproval ? <ShieldAlert className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />)}
                {action.requiresApproval ? "Accept → request approval" : "Accept & apply"}
              </button>
              <button onClick={() => onDismiss(rec)} disabled={busy === rec.id}
                className="inline-flex items-center gap-1.5 border border-border rounded-lg px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:bg-muted disabled:opacity-50"
                data-testid={`rec-dismiss-${rec.id}`}>
                <X className="h-3.5 w-3.5" /> Dismiss
              </button>
            </div>
          )}
          {!isPending && (
            <span className="inline-block mt-2 text-[11px] font-semibold capitalize text-muted-foreground" data-testid={`rec-status-${rec.id}`}>Status: {rec.status}</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function RecommendationsPage() {
  const scope = useTenantScope();
  const { isSuper, tenantId, params } = scope;
  const [agentCode, setAgentCode] = useState("");
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  const blocked = isSuper && !tenantId;

  const load = useCallback(() => {
    if (blocked) { setItems([]); return; }
    setLoading(true);
    aiAgentService.recommendations({ ...params, status, agentCode: agentCode || undefined, size: 100 })
      .then((d) => setItems(d.items || []))
      .catch((e) => toast.error(apiErrorMessage(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [blocked, params, status, agentCode]);

  useEffect(() => { load(); }, [load]);

  const accept = async (rec) => {
    setBusy(rec.id);
    try {
      const r = await aiAgentService.acceptRec(rec.id);
      if (r.requiresApproval) {
        toast.success("Accepted — sent to the Approvals queue for human sign-off");
      } else {
        if (rec.action?.id) await aiAgentService.executeAction(rec.action.id);
        toast.success("Accepted and applied");
      }
      load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const dismiss = async (rec) => {
    setBusy(rec.id);
    try { await aiAgentService.dismissRec(rec.id); toast.success("Dismissed"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="recommendations-page">
      <PageHeader icon={Lightbulb} title="Recommendations"
        subtitle="Review what your agents found. Low-risk actions apply on accept; high-risk go to approval.">
        <TenantPicker {...scope} />
      </PageHeader>

      {blocked ? <SelectTenantNotice /> : (
        <>
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <div className="flex bg-muted/40 rounded-lg p-1" data-testid="rec-status-tabs">
              {STATUSES.map((s) => (
                <button key={s} onClick={() => setStatus(s)}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold capitalize transition-colors ${status === s ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid={`rec-tab-${s}`}>{s}</button>
              ))}
            </div>
            <select value={agentCode} onChange={(e) => setAgentCode(e.target.value)}
              className="bg-background border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" data-testid="rec-agent-filter">
              {AGENTS.map((a) => <option key={a.code} value={a.code}>{a.label}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : items.length === 0 ? (
            <div className="bg-card border border-border rounded-xl p-12 text-center" data-testid="rec-empty">
              <Lightbulb className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No {status} recommendations. Run an agent from the Agent Center to generate fresh ones.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="rec-list">
              {items.map((rec) => <RecCard key={rec.id} rec={rec} onAccept={accept} onDismiss={dismiss} busy={busy} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
